import { useEffect, useState } from 'react';
import { Wifi, WifiOff, CheckCircle } from 'lucide-react';
import useOnlineStatus from '../hooks/useOnlineStatus';

/**
 * Sticky banner that appears at the top of the page when the user goes offline,
 * and briefly shows a green confirmation when the connection comes back.
 */
export default function OfflineBanner() {
    const { online, lastChangedAt } = useOnlineStatus();
    const [showReconnect, setShowReconnect] = useState(false);
    const [hasBeenOffline, setHasBeenOffline] = useState(false);

    useEffect(() => {
        if (!online) {
            setHasBeenOffline(true);
            setShowReconnect(false);
            return;
        }
        if (online && hasBeenOffline) {
            setShowReconnect(true);
            const t = setTimeout(() => setShowReconnect(false), 3500);
            return () => clearTimeout(t);
        }
    }, [online, hasBeenOffline, lastChangedAt]);

    if (online && !showReconnect) return null;

    if (!online) {
        return (
            <div
                role="alert"
                data-testid="offline-banner"
                className="sticky top-0 z-[60] w-full bg-amber-500 text-white shadow-md animate-in fade-in"
            >
                <div className="max-w-screen-2xl mx-auto px-4 py-2.5 flex items-center justify-center gap-3 text-sm font-medium">
                    <WifiOff className="w-4 h-4 shrink-0" />
                    <span>
                        Hors-ligne — vos modifications sont sauvegardées localement et seront envoyées à la reconnexion.
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div
            role="status"
            data-testid="online-banner"
            className="sticky top-0 z-[60] w-full bg-emerald-500 text-white shadow-md animate-in fade-in"
        >
            <div className="max-w-screen-2xl mx-auto px-4 py-2.5 flex items-center justify-center gap-3 text-sm font-medium">
                <CheckCircle className="w-4 h-4 shrink-0" />
                <span>Connexion rétablie</span>
                <Wifi className="w-4 h-4 shrink-0 opacity-80" />
            </div>
        </div>
    );
}
